import * as Types from '@xing-com/crate-common-graphql-types';

import type {
  GetGameDreamJobsQuery,
  GetSearchAndShuffleQuery,
  GetDreamMachineVompExperimentQuery,
  GetDreamMachineProfileSkillsQuery,
  DreamJobItemFragment,
  OccupiedJobFragment,
  AlertDreamJobMutation,
  DeleteAlertDreamJobMutation,
  DeleteBookmarkDreamJobMutation,
  DreamJobsSetConsentDetailsMutation,
  DreamJobsInteractMutation,
  DreamJobsUpdateInteractMutation,
  DreamJobDeleteInteractionMutation,
  BookmarkDreamJobMutation,
  DreamInternalProfileVisitMutation,
  UpdateDreamMachineSkillsMutation,
  GetTakenJobDetailsQueryQuery,
  GetDreamMachineVisitorsQuery,
  GetSsYourJobsTakenQuery,
  GetSsYourJobsBookmarkedQuery,
} from '.';

const createIncrementalIdGenerator = (): (() => string) => {
  let idCounter = 0; // Initialize the counter

  return (): string => {
    idCounter += 1; // Increment the counter
    return 'id' + idCounter; // Return the current value
  };
};

// Create the generator instance
export const generateIncrementalId = createIncrementalIdGenerator();

export const getDreamJobsQueryMocks = (): GetGameDreamJobsQuery => ({
  viewer: {
    jobWishesPreference: {
      industries: [
        {
          id: '90100.a8b7b7',
          localizationValue: 'Retail',
          __typename: 'Industry',
        },
      ],
    },
  },
  dreamJobsFetchRecommendation: {
    total: 0,
    collection: getMockedCollection(),
    __typename: 'DreamJobsCollection',
  },
});

export const getSearchAndSwipeMocks = (
  recoType: string
): GetSearchAndShuffleQuery => ({
  viewer: {
    jobWishesPreference: {
      industries: [
        {
          id: '90100.a8b7b7',
          localizationValue: 'Retail',
          __typename: 'Industry',
        },
      ],
    },
  },
  dreamJobsFetchSearchSwipeRecommendation: {
    total: 0,
    collection: getMockedCollection().filter((item) => {
      if (recoType === Types.RecommendationType.Available) {
        return item.__typename === 'AvailableJob' ? item : false;
      }

      if (recoType === Types.RecommendationType.Taken) {
        return item.__typename === 'TakenJob' ? item : false;
      }

      return item;
    }),
    __typename: 'DreamJobsCollection',
  },
  dreamJobsFetchConsentDetails: {
    consentTaken: false,
    __typename: 'DreamJobsConsentFetchSuccess',
  },
});

export const getDreamJobsQueryEmptyMocks = (): GetGameDreamJobsQuery => ({
  viewer: {
    jobWishesPreference: {
      industries: [
        {
          id: '90100.a8b7b7',
          localizationValue: 'Retail',
          __typename: 'Industry',
        },
      ],
    },
  },
  dreamJobsFetchRecommendation: {
    total: 0,
    collection: [],
    __typename: 'DreamJobsCollection',
  },
});

const skills = [
  'html',
  'linux',
  'windows',
  'administration',
  'ms-sql',
  'crystal reports',
  'it support',
  'oracle sql',
  'informatik',
  'mobile device management',
  'erp',
  'englische sprache',
  'systemadministration',
  'data exchanger',
  'windows server grundlagen',
  'programmierung',
  'testing',
  'pc',
  'haskell',
  'python',
  'software-rollout',
  'datenbank',
  'ms visio',
  'pl/sql',
  'it-anwendungen',
  'pc support',
  'anwendungsbetreuung',
  'helpdesk',
  'oracle db',
  'microsoft office 365',
  'grundlagen php',
  'sql',
  'java',
];

const mockSkills = (
  matched: number,
  notMatched: number
): Pick<
  OccupiedJobFragment,
  | 'skillsMatchedCount'
  | 'skillsNotMatchedCount'
  | 'skillsTotal'
  | 'skillsMatched'
  | 'skillsNotMatched'
> => ({
  skillsMatchedCount: matched,
  skillsNotMatchedCount: notMatched,
  skillsTotal: matched + notMatched,
  skillsMatched: skills.slice(0, matched).map((skill) => ({
    skill,
    __typename: 'DreamJobSkills' as const,
  })),
  skillsNotMatched: skills
    .slice(matched + 1, matched + notMatched)
    .map((skill) => ({
      skill,
      __typename: 'DreamJobSkills' as const,
    })),
});

const mockSkillsForAvailableJob = (
  matched: number,
  notMatched: number
): Types.OntologyUserJobPostingProfileMatchResponse => ({
  skills: {
    matchedSkills: skills.slice(0, matched).map((skill) => ({
      label: skill,
      language: Types.OntologyLanguage.En,
      isSoft: false,
      __typename: 'OntologyLocalizedJobPostingSkill',
    })),
    notMatchedSkills: skills
      .slice(matched + 1, matched + notMatched)
      .map((skill) => ({
        label: skill,
        language: Types.OntologyLanguage.En,
        isSoft: false,
        __typename: 'OntologyLocalizedJobPostingSkill',
      })),
  },
});

const mockSimilarMembers = (
  count: number,
  isLong?: boolean
): Pick<OccupiedJobFragment, 'similarMembers' | 'vompMembers'> => ({
  similarMembers: [
    {
      user: {
        id: '10371697.abc',
        displayName: 'Andreas Taube',
        gender: Types.Gender.Male,
        profileImage: [
          {
            url: '/test-assets/profile-image-1.jpeg',
            __typename: 'ProfileImage' as const,
          },
          {
            url: '/test-assets/profile-image-1.jpeg',
            __typename: 'ProfileImage' as const,
          },
        ],
        location: {
          city: 'Hamburg',
          displayLocation: '**Hamburg**, Germany',
          __typename: 'XingIdLocation' as const,
        },
        userFlags: {
          displayFlag: Types.UserFlagsDisplayFlag.Premium,
          __typename: 'UserFlags',
        },
        occupations: [
          {
            subline: 'VP of Engineering',
            __typename: 'XingIdOccupation' as const,
          },
        ],
        pageName: 'Andreas_Taube3',
        __typename: 'XingId' as const,
      },
      __typename: 'DreamJobSimilarMembers' as const,
    },
    ...Array.from({ length: count - 1 }, (_, i) => ({
      user: {
        id: `user${i + 2}.abc`,
        displayName: `User ${i + 2}` + 'e'.repeat(isLong ? 1000 : 0),
        gender: Types.Gender.Male,
        profileImage: [
          {
            url: `/test-assets/profile-image-${((i + 1) % 4) + 1}.jpeg`,
            size: Types.ProfileImageSize.Square_48,
            __typename: 'ProfileImage' as const,
          },
          {
            url: `/test-assets/profile-image-${((i + 1) % 4) + 1}.jpeg`,
            size: Types.ProfileImageSize.Square_96,
            __typename: 'ProfileImage' as const,
          },
        ],

        location: {
          city: `City ${i + 2} ` + 'e'.repeat(isLong ? 1000 : 0),
          displayLocation:
            `**City ${i + 2}**, Country ` + 'e'.repeat(isLong ? 1000 : 0),
          __typename: 'XingIdLocation' as const,
        },
        occupations: [
          {
            subline: `Occupation ${i + 2} ` + 'e'.repeat(isLong ? 1000 : 0),
            __typename: 'XingIdOccupation' as const,
          },
        ],
        userFlags: {
          displayFlag: Types.UserFlagsDisplayFlag.Basic,
          __typename: 'UserFlags' as const,
        },
        pageName: `User_${i + 2}`,
        __typename: 'XingId' as const,
      },
      __typename: 'DreamJobSimilarMembers' as const,
    })),
  ],
  vompMembers: [
    {
      userId: '10371697.abc',
      __typename: 'DreamJobSimilarMembers',
    },
    {
      userId: 'user2.abc',
      __typename: 'DreamJobSimilarMembers',
    },
  ],
});

// dream machine cards mock data
// card 1: available card data
// card 2: perfect data for single member
// card 3: perfect data for multiple members
// card 4: maximum data
// card 5: minimum data
const getMockedCollection: () => DreamJobItemFragment[] = () => [
  // card 1 - AvailableJob
  {
    __typename: 'AvailableJob',
    id: generateIncrementalId(),
    createdAt: '2021-09-01T12:00:00Z',
    availableJobUrn:
      'urn:x-xing:jobs:posting:26915631' + generateIncrementalId(),
    availableJobTrackingToken:
      '0ccb2c8ca1bc11ef95009ece05fe5fa4.5.125703268' + generateIncrementalId(),
    job: {
      visibleJobId: generateIncrementalId(),
      slug: 'brandenburg-havel-cto-full-stack-developer-needed-for-digital-health-startup-67789215',
      refreshedAt: '2019-08-13T02:02:01.000Z',
      __typename: 'VisibleJob',
      title: 'VP Product and Strategy',
      paid: false,
      companyInfo: {
        companyNameOverride: 'ZF',
        company: {
          __typename: 'Company',
          companyName: 'ZF Group',
          entityPageId: generateIncrementalId(),
          entityPage: {
            id: generateIncrementalId(),
            slug: 'zf-group',
            coverImage: [
              {
                url: '/test-assets/header-desktop-984x480.png',
              },
            ],
          },
          logos: {
            logo256px: '/test-assets/256x256xdddx000.png',
          },
          address: {
            city: 'CityVorchdorfDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialist',
          },
          kununuData: {
            companyProfileUrl: 'https://www.kununu.com/de/new-work',
            ratingCount: 2781123,
            ratingAverage: 4.2,
          },
          aboutUsBatched: {
            __typename: 'AboutEntity',
            aboutHeadline: null,
            aboutSummary:
              'Mit ZF Innovation jeden Tag erleben ZF ist ein weltweit aktiver Technologiekonzern und liefert Systeme für die Mobilität von Pkw, Nutzfahrzeugen und Industrietechnik. ZF lässt Fahrzeuge sehen, denken und handeln: In den vier Technologiefeldern Vehicle Motion Control, integrierte Sicherheit, automatisiertes Fahren und Elektromobilität bietet ZF umfassende Lösungen für etablierte Fahrzeughersteller und Mobilitätsanbieter sowie für Menschen, die ihre Mobilität individuell gestalten möchten. ',
          },
        },
      },
      availableJobLocation: {
        city: 'Friedrichshafen',
      },
      salary: {
        __typename: 'SalaryEstimate',
        minimum: 47000,
        maximum: 150000,
        median: 65000,
        currency: Types.Currency.Eur,
      },
      matchingHighlights: {
        highlight: null,
        matchingFacts: [
          {
            localization: {
              localizationValue: 'EUR 3505 - EUR 9505',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            localizationA11y: {
              localizationValue: 'EUR 3505 - EUR 9505',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            type: 'job_keyfact_salary',
            __typename: 'JobMatchingHighlightsJobKeyfactSalaryV2',
          },
          {
            localization: {
              localizationValue: 'Fact matched number 1',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            type: 'job_keyfact_employment_type',
            __typename: 'JobMatchingHighlightsJobKeyfactStringV2',
          },
          {
            localization: {
              localizationValue: 'Fact  number 2',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            type: 'job_keyfact_employment_type',
            __typename: 'JobMatchingHighlightsJobKeyfactStringV2',
          },
          {
            localization: {
              localizationValue: 'Fact matched number 3',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            type: 'job_keyfact_employment_type',
            __typename: 'JobMatchingHighlightsJobKeyfactStringV2',
          },
        ],
        nonMatchingFacts: [
          {
            localization: {
              localizationValue: 'Full-time',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            type: 'job_keyfact_employment_type',
            __typename: 'JobMatchingHighlightsJobKeyfactStringV2',
          },
          {
            localization: {
              localizationValue: 'Fact number 1',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            type: 'job_keyfact_employment_type',
            __typename: 'JobMatchingHighlightsJobKeyfactStringV2',
          },
          {
            localization: {
              localizationValue: 'Fact  number 2',
              __typename: 'JobMatchingHighlightsLocalizableV2',
            },
            type: 'job_keyfact_employment_type',
            __typename: 'JobMatchingHighlightsJobKeyfactStringV2',
          },
        ],

        __typename: 'JobMatchingHighlightsV2',
      },
      userProfileMatches: mockSkillsForAvailableJob(12, 10),
    },
  },
  // card 2
  {
    __typename: 'TakenJob',
    id: generateIncrementalId(),
    createdAt: '2021-09-02T12:00:00Z',
    takenJobUrn: 'urn:x-xing:jobs:taken:' + generateIncrementalId(),
    takenJobTrackingToken:
      '0ccb2c8ca1bc11ef95009ece05fe5fa4.0.4407929121461524645',
    job: {
      __typename: 'OccupiedJob',
      id: generateIncrementalId(),
      urn: 'urn:x-xing:jobs:taken:1',
      trackingToken: null,
      jobTitle: 'Database And Migration Specialist',
      companyId: '28308.3acbdc',
      company: {
        __typename: 'Company',
        companyName: 'LORENZ Life Sciences Group',
        entityPageId: generateIncrementalId(),
        entityPage: {
          id: generateIncrementalId(),
          slug: 'lorenz-life-sciences-group',
          coverImage: [
            {
              url: '/test-assets/header-desktop-984x480.png',
            },
          ],
        },
        industry: {
          localizationValue: 'Retail',
          __typename: 'Industry',
        },
        logos: {
          logo256px: '/test-assets/256x256xdddx000.png',
        },
        address: {
          city: 'Gutach im Breisgau',
        },
        kununuData: {
          companyProfileUrl: 'https://www.kununu.com/de/new-work',
          ratingCount: 881,
          ratingAverage: 3.5,
        },
        aboutUs: {
          __typename: 'AboutEntity',
          aboutHeadline: 'About us',
          aboutSummary:
            'LORENZ Life Sciences Group empowers the pharmaceutical industry with innovative regulatory solutions, streamlining compliance and accelerating product approvals worldwide.',
        },
      },
      fallbackCompany: null,
      takenJobLocation: null,
      ...mockSkills(12, 10),
      experienceMatchedScore: 4,
      experienceTotal: 8,
      preferredIndustry: {
        id: '90100.a8b7b7',
        value: 'Software',
        __typename: 'DreamJobIndusty',
      },
      preferredDiscipline: {
        id: null,
        value: 'ITSoftwareDevelopment',
        __typename: 'DreamJobDiscipline',
      },
      ...mockSimilarMembers(1),
      salary: {
        currency: Types.Currency.Eur,
        minimum: 100000,
        median: 150000,
        maximum: 200000,
        __typename: 'SalaryEstimate',
      },
    },
  },
  // card 3
  {
    __typename: 'TakenJob',
    id: generateIncrementalId(),
    createdAt: '2015-09-03T12:00:00Z',
    takenJobUrn: 'urn:x-xing:jobs:taken:1' + generateIncrementalId(),
    takenJobTrackingToken:
      '0ccb2c8ca1bc11ef95009ece05fe5fa4.0.4407929121461524645',
    job: {
      __typename: 'OccupiedJob',
      id: generateIncrementalId(),
      urn: 'urn:x-xing:jobs:taken:1',
      trackingToken: null,
      jobTitle: 'Senior Site Reliability Engineer (m/f/d)',
      companyId: '28308.3acbdc',
      company: {
        __typename: 'Company',
        companyName: 'sixt',
        entityPageId: generateIncrementalId(),
        entityPage: {
          id: generateIncrementalId(),
          slug: 'sixt',
          coverImage: [
            {
              url: '/test-assets/header-desktop-984x480.png',
            },
          ],
        },
        industry: {
          localizationValue: 'Retail',
          __typename: 'Industry',
        },
        logos: {
          logo256px: '/test-assets/256x256xdddx000.png',
        },
        address: {
          city: 'Pullach im Isartal',
        },
        kununuData: {
          ratingCount: 881,
        },
        aboutUsBatched: {
          __typename: 'AboutEntity',
          aboutHeadline: 'Über uns',
          aboutSummary:
            'XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt.\n\nXING steht für mehr ... dfdf',
        },
      },
      fallbackCompany: null,
      takenJobLocation: null,
      ...mockSkills(10, 12),
      experienceMatchedScore: 4,
      experienceTotal: 8,
      preferredIndustry: {
        id: '90100.a8b7b7',
        value: 'Software',
        __typename: 'DreamJobIndusty',
      },
      preferredDiscipline: {
        id: null,
        value: 'ITSoftwareDevelopment',
        __typename: 'DreamJobDiscipline',
      },
      ...mockSimilarMembers(3),
      salary: {
        currency: Types.Currency.Eur,
        minimum: 100000,
        median: 150000,
        maximum: 200000,
        __typename: 'SalaryEstimate',
      },
    },
  },
  // card 4
  {
    __typename: 'TakenJob',
    id: generateIncrementalId(),
    takenJobUrn: 'urn:x-xing:jobs:taken:' + generateIncrementalId(),
    createdAt: '2015-09-03T12:00:00Z',
    takenJobTrackingToken:
      '0ccb2c8ca1bc11ef95009ece05fe5fa4.0.4407929121461524645',
    job: {
      __typename: 'OccupiedJob',
      id: generateIncrementalId(),
      urn: 'urn:x-xing:jobs:taken:1',
      trackingToken: null,
      jobTitle:
        'JobTitleDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialist',
      companyId: '28308.3acbdc',
      company: {
        __typename: 'Company',
        companyName:
          'CompanyNameDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialist',
        entityPageId: generateIncrementalId(),
        entityPage: {
          id: generateIncrementalId(),
          slug: 'xing-preview',
          coverImage: [
            {
              url: '/test-assets/header-desktop-984x480.png',
            },
          ],
        },
        industry: {
          localizationValue: 'Automotive, motorcycles and bicycles',
          __typename: 'Industry',
        },
        logos: {
          logo256px: '/test-assets/256x256xdddx000.png',
        },
        address: {
          city: 'CityVorchdorfDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialist',
        },
        kununuData: {
          ratingCount: 2781123,
        },
        aboutUsBatched: {
          __typename: 'AboutEntity',
          aboutHeadline:
            'Über uns Über uns Über uns Über uns Über uns Über uns Über uns Über uns Über uns Über uns ',
          aboutSummary:
            'XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt.  XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt.  XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt.  XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt. XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt. XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt. ',
        },
      },
      fallbackCompany: null,
      takenJobLocation: 'Laakirchen',
      ...mockSkills(20, 10),
      experienceMatchedScore: null,
      experienceTotal: null,
      preferredIndustry: {
        id: '30100.2d3dde',
        value:
          'industryDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialist',
        __typename: 'DreamJobIndusty',
      },
      preferredDiscipline: {
        id: null,
        value: 'ITSoftwareDevelopment',
        __typename: 'DreamJobDiscipline',
      },
      ...mockSimilarMembers(30),
      salary: {
        currency: Types.Currency.Eur,
        minimum: 0,
        median: 750000,
        maximum: 99999999,
        __typename: 'SalaryEstimate',
      },
    },
  },
  // card 5
  {
    __typename: 'TakenJob',
    id: generateIncrementalId(),
    createdAt: '2015-09-03T12:00:00Z',
    takenJobUrn: 'urn:x-xing:jobs:taken:' + generateIncrementalId(),
    takenJobTrackingToken:
      '0ccb2c8ca1bc11ef95009ece05fe5fa4.0.4407929121461524645',
    job: {
      __typename: 'OccupiedJob',
      id: generateIncrementalId(),
      urn: 'urn:x-xing:jobs:taken:1',
      trackingToken: null,
      jobTitle: 'Title',
      companyId: '28308.3acbdc',
      company: null,
      fallbackCompany: null,
      takenJobLocation: null,
      ...mockSkills(0, 10),
      experienceMatchedScore: null,
      experienceTotal: null,
      preferredIndustry: null,
      preferredDiscipline: null,
      ...mockSimilarMembers(1),
      salary: null,
    },
  },
  // // card 6 - AvailableJob - minumum data
  // {
  //   __typename: 'AvailableJob',
  //   id: generateIncrementalId(),
  //   createdAt: '2021-09-01T12:00:00Z',
  //   availableJobUrn:
  //     'urn:x-xing:jobs:posting:26915631' + generateIncrementalId(),
  //   availableJobTrackingToken:
  //     '0ccb2c8ca1bc11ef95009ece05fe5fa4.5.125703268' + generateIncrementalId(),
  //   job: {
  //     visibleJobId: generateIncrementalId(),
  //     slug: 'brandenburg-havel-cto-full-stack-developer-needed-for-digital-health-startup-67789215',
  //     refreshedAt: '2019-08-13T02:02:01.000Z',
  //     __typename: 'VisibleJob',
  //     title: 'VP Product and Strategy',
  //     companyInfo: {
  //       companyNameOverride: 'ZF',
  //       company: {
  //         __typename: 'Company',
  //         companyName: 'ZF Group',
  //         entityPageId: generateIncrementalId(),
  //         entityPage: {
  //           id: generateIncrementalId(),
  //           slug: 'zf-group',
  //           coverImage: [
  //             {
  //               url: '/test-assets/header-desktop-984x480.png',
  //             },
  //           ],
  //         },
  //         logos: {
  //           logo256px: '/test-assets/256x256xdddx000.png',
  //         },
  //         address: {
  //           city: 'CityVorchdorfDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialistDatabaseAndMigrationSpecialist',
  //         },
  //         kununuData: null,
  //         aboutUsBatched: {
  //           __typename: 'AboutEntity',
  //           aboutHeadline: null,
  //           aboutSummary:
  //             'XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt.  XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt.  XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt.  XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt. XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt. XING als führendes Jobs-Netzwerk im deutschsprachigen Raum unterstützt seine mehr als 21 Mio. Mitglieder aus Deutschland, Österreich und der Schweiz dabei, so arbeiten zu können, wie es zu ihren persönlichen Lebensentwürfen und Wünschen passt. ',
  //         },
  //       },
  //     },
  //     availableJobLocation: {
  //       city: 'Friedrichshafen',
  //     },
  //     salary: {
  //       __typename: 'SalaryEstimate',
  //       minimum: 20000,
  //       maximum: 40000,
  //       median: 40000,
  //       currency: Types.Currency.Eur,
  //     },
  //   },
  //   skillsTotal: 0,
  //   skillsMatchedCount: 0,
  //   skillsNotMatchedCount: 0,
  //   skillsMatched: [],
  //   skillsNotMatched: [],
  // },
];

export const getSsYourJobsTakenQueryMocks = (): GetSsYourJobsTakenQuery => ({
  superliked: {
    collection: [
      {
        __typename: 'TakenJob',
        id: generateIncrementalId(),
        createdAt: '2021-09-01T12:00:00Z',
        takenJobUrn: 'urn:x-xing:jobs:taken:' + generateIncrementalId(),
        takenJobTrackingToken: 'trackingToken' + generateIncrementalId(),
        job: {
          __typename: 'OccupiedJob',
          urn: 'urn:x-xing:jobs:taken:' + generateIncrementalId(),
          trackingToken: 'trackingToken' + generateIncrementalId(),
          jobTitle: 'Database And Migration Specialist',
          companyId: '28308.3acbdc',
          takenJobId: 'id' + generateIncrementalId(),
          takenJobLocation: 'Porto',
          company: {
            __typename: 'Company',
            companyName: 'ZF Group',
            logos: {
              __typename: 'CompanyLogos',
              logo256px: '/test-assets/256x256xdddx000.png',
            },
            address: {
              __typename: 'CompanyAddress',
              city: 'Porto',
            },
          },
        },
      },
      {
        __typename: 'TakenJob',
        id: generateIncrementalId(),
        createdAt: '2025-07-22T12:00:00Z',
        takenJobUrn: 'urn:x-xing:jobs:taken:' + generateIncrementalId(),
        takenJobTrackingToken: 'trackingToken' + generateIncrementalId(),
        job: {
          __typename: 'OccupiedJob',
          urn: 'urn:x-xing:jobs:taken:' + generateIncrementalId(),
          trackingToken: 'trackingToken' + generateIncrementalId(),
          jobTitle: 'Database And Migration Specialist',
          companyId: '28308.3acbdc',
          takenJobId: 'id' + generateIncrementalId(),
          takenJobLocation: 'Porto',
          company: {
            __typename: 'Company',
            companyName: 'Xing',
            logos: {
              __typename: 'CompanyLogos',
              logo256px: '/test-assets/256x256xdddx000.png',
            },
            address: {
              __typename: 'CompanyAddress',
              city: 'Porto',
            },
          },
        },
      },
    ],
    __typename: 'DreamJobsCollection',
    total: 0,
  },
  viewer: {
    id: '11655360.ca5196',
    searchAlerts: {
      __typename: 'SearchAlertResultResponse',
      collection: [
        {
          id: '9756.1d13d7',
          queryId: '8f7dc69c311b1f0b04c8bdcbfb6765d2',
          query: {
            body: {
              keywords: 'Database And Migration Specialist ZF Group',
              location: {
                text: 'Porto',
              },
            },
          },
        },
        {
          id: '9756.1d13d7',
          queryId: '8f7dc69c311b1f0b04c8bdcbfb6765d2',
          query: {
            body: {
              keywords: 'Machine Learning Engineer Test cambio title',
              location: {
                text: 'Porto',
              },
            },
          },
        },
      ],
    },
  },
});

export const getSsYourJobsBookmarkedQueryMocks =
  (): GetSsYourJobsBookmarkedQuery => ({
    viewer: {
      id: '25916928.0bee4a',
      jobBookmarks: {
        total: 4,
        pageInfo: {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: 'WzE3NTMyNjMyMzEwMDQ0NTEsMTU0MjQwNV0=',
          endCursor: 'WzE3NTE5MDMxMjg4NjA3MTcsMTIyODEzNl0=',
          __typename: 'PageInfo',
        },
        edges: [
          {
            node: {
              state: Types.JobBookmarkState.Saved,
              updatedAt: '2025-07-23T09:33:51.004Z',
              job: {
                id: '137981015.2ff9f4',
                slug: 'nuernberg-inhouse-teamleitung-microsoft-operations-all-genders-137981015',
                url: 'https://www.xing.com/jobs/nuernberg-inhouse-teamleitung-microsoft-operations-all-genders-137981015',
                title: 'Inhouse Teamleitung Microsoft Operations (all genders)',
                globalId: 'surn:x-xing:jobs:posting:137981015.2ff9f4',
                location: {
                  city: 'Nürnberg',
                  __typename: 'JobLocation',
                },
                companyInfo: {
                  companyNameOverride: 'adesso SE',
                  company: {
                    id: '28375.dd72a9',
                    companyName: 'adesso SE',
                    logos: {
                      x1: '/test-assets/256x256xdddx000.png',
                      __typename: 'CompanyLogos',
                    },
                    __typename: 'Company',
                  },
                  __typename: 'JobCompanyInfo',
                },
                __typename: 'VisibleJob',
              },
              __typename: 'JobBookmark',
            },
            __typename: 'JobBookmarkEdge',
          },
          {
            node: {
              state: Types.JobBookmarkState.Saved,
              updatedAt: '2025-07-22T08:05:08.123Z',
              job: {
                id: '137981005.cd7a14',
                slug: 'duesseldorf-inhouse-teamleitung-microsoft-operations-all-genders-137981005',
                url: 'https://www.xing.com/jobs/duesseldorf-inhouse-teamleitung-microsoft-operations-all-genders-137981005',
                title: 'Inhouse Teamleitung Microsoft Operations (all genders)',
                globalId: 'surn:x-xing:jobs:posting:137981005.cd7a14',
                location: {
                  city: 'Düsseldorf',
                  __typename: 'JobLocation',
                },
                companyInfo: {
                  companyNameOverride: 'adesso SE',
                  company: {
                    id: '28375.dd72a9',
                    companyName: 'adesso SE',
                    logos: {
                      x1: '/test-assets/256x256xdddx000.png',
                      __typename: 'CompanyLogos',
                    },
                    __typename: 'Company',
                  },
                  __typename: 'JobCompanyInfo',
                },
                __typename: 'VisibleJob',
              },
              __typename: 'JobBookmark',
            },
            __typename: 'JobBookmarkEdge',
          },
          {
            node: {
              state: Types.JobBookmarkState.Saved,
              updatedAt: '2025-07-22T08:03:16.352Z',
              job: {
                id: '137981026.7cc8a3',
                slug: 'rostock-inhouse-teamleitung-microsoft-operations-all-genders-137981026',
                url: 'https://www.xing.com/jobs/rostock-inhouse-teamleitung-microsoft-operations-all-genders-137981026',
                title: 'Inhouse Teamleitung Microsoft Operations (all genders)',
                globalId: 'surn:x-xing:jobs:posting:137981026.7cc8a3',
                location: {
                  city: 'Rostock',
                  __typename: 'JobLocation',
                },
                companyInfo: {
                  companyNameOverride: 'adesso SE',
                  company: {
                    id: '28375.dd72a9',
                    companyName: 'adesso SE',
                    logos: {
                      x1: '/test-assets/256x256xdddx000.png',
                      __typename: 'CompanyLogos',
                    },
                    __typename: 'Company',
                  },
                  __typename: 'JobCompanyInfo',
                },
                __typename: 'VisibleJob',
              },
              __typename: 'JobBookmark',
            },
            __typename: 'JobBookmarkEdge',
          },
          {
            node: {
              state: Types.JobBookmarkState.Saved,
              updatedAt: '2025-07-07T15:45:28.860Z',
              job: {
                id: '135908171.751d81',
                slug: 'porto-senior-frontend-engineer-135908171',
                url: 'https://www.xing.com/jobs/porto-senior-frontend-engineer-135908171',
                title: 'Senior Frontend Engineer (m/f/d)',
                globalId: 'surn:x-xing:jobs:posting:135908171.751d81',
                location: {
                  city: 'Porto',
                  __typename: 'JobLocation',
                },
                companyInfo: {
                  companyNameOverride: 'kununu',
                  company: {
                    id: '141096.f8e039',
                    companyName: 'kununu – part of New Work SE',
                    logos: {
                      x1: '/test-assets/256x256xdddx000.png',
                      __typename: 'CompanyLogos',
                    },
                    __typename: 'Company',
                  },
                  __typename: 'JobCompanyInfo',
                },
                __typename: 'VisibleJob',
              },
              __typename: 'JobBookmark',
            },
            __typename: 'JobBookmarkEdge',
          },
        ],
        __typename: 'JobBookmarksConnection',
      },
      __typename: 'Viewer',
    },
  });

export const getDreamMachineVompExperimentQueryMocks = (
  userId: string
): GetDreamMachineVompExperimentQuery => {
  if (userId === '10371697') {
    return {
      featureSwitchesLoggedOutExperiments: {
        collection: [
          {
            name: 'ABACUS-477',
            variant: 'B',
            __typename: 'FeatureSwitchesExperiment',
          },
        ],
        __typename: 'FeatureSwitchesExperimentsCollection',
      },
    };
  }
  return {
    featureSwitchesLoggedOutExperiments: null,
  };
};

export const getDreamJobsSetConsentDetailsResultMocks =
  (): DreamJobsSetConsentDetailsMutation => ({
    dreamJobsSetConsentDetails: {
      userId: '1',
      updatedAt: '2025-07-17T10:12:24.436644908Z',
    },
  });

export const getAlertDreamJobMutationResultMocks =
  (): AlertDreamJobMutation => ({
    createSearchAlertByQueryNew: {
      __typename: 'SearchAlertCreationSuccess',
      alert: {
        id: '9249.5ca897',
      },
    },
  });

export const getBookmarkDreamJobMutationResultMocks =
  (): BookmarkDreamJobMutation => ({
    jobUpsertBookmark: {
      __typename: 'JobBookmark',
    },
  });

export const getDeleteBookmarkDreamJobMocks =
  (): DeleteBookmarkDreamJobMutation => ({
    jobDeleteBookmark: {
      job: {
        __typename: 'VisibleJob',
        id: '139438098.8d3805',
      },
      error: null,
      __typename: 'JobBookmarkMutationResult',
    },
  });

export const getDeleteAlertDreamJobMocks = (): DeleteAlertDreamJobMutation => ({
  deleteSearchAlert: {
    __typename: 'Empty',
    stub: 'stub',
  },
});

export const getDreamJobsInteractResultMocks =
  (): DreamJobsInteractMutation => ({
    dreamJobStoreInteraction: {
      __typename: 'DreamJobsStoreSuccess',
      jobId: '1',
      createdAt: '2021-09-01T12:00:00Z',
    },
  });

export const getDreamJobsUpdateInteractResultMocks =
  (): DreamJobsUpdateInteractMutation => ({
    dreamJobUpdateInteraction: {
      __typename: 'DreamJobsStoreSuccess',
      jobId: '1',
      createdAt: '2021-09-01T12:00:00Z',
    },
  });

export const getDreamJobDeleteInteractResultMocks =
  (): DreamJobDeleteInteractionMutation => ({
    dreamJobDeleteInteraction: {
      __typename: 'DreamJobsDeleteSuccess',
      jobId: '1',
    },
  });

export const getDreamInternalProfileVisitMutationMocks =
  (): DreamInternalProfileVisitMutation => ({
    internalProfileVisit: {
      __typename: 'InternalVompResult',
      success: {
        __typename: 'InternalVompSuccess',
        value: 'done',
      },
      error: null,
    },
  });

export const getDreamMachineProfileSkillsQueryMocks = (
  userId: string
): GetDreamMachineProfileSkillsQuery => ({
  profileSkills: {
    globalId: `surn:x-xing:profiles:profile:${userId}:skills`,
    collection: [
      {
        value: 'PC',
        isTop: false,
        category: 'hard',
      },
      {
        value: 'React',
        isTop: false,
        category: 'hard',
      },
      {
        value: 'REST',
        isTop: false,
        category: 'hard',
      },
      {
        value: 'Agile Development',
        isTop: false,
        category: 'hard',
      },
    ],
  },
});

export const getUpdateDreamMachineSkillsMutationMocks = (
  userId: string
): UpdateDreamMachineSkillsMutation => ({
  profileSkillsUpdate: {
    profileSkills: {
      globalId: `urn:x-xing:profiles:profile:${userId}:skills`,
      collection: [
        {
          value: 'PC',
          isTop: false,
          category: 'hard',
        },
        {
          value: 'React',
          isTop: false,
          category: 'hard',
        },
        {
          value: 'REST',
          isTop: false,
          category: 'hard',
        },
        {
          value: 'Agile Development',
          isTop: false,
          category: 'hard',
        },
      ],
    },
    error: null,
  },
});

export const getTakenJobDetailsQueryMocks =
  (): GetTakenJobDetailsQueryQuery => ({
    dreamJobsFetchTakenJobDetails: [
      {
        __typename: 'OccupiedJob',
        id: generateIncrementalId(),
        urn: 'urn:x-xing:jobs:taken:1',
        trackingToken: null,
        jobTitle: 'Database And Migration Specialist',
        companyId: '28308.3acbdc',
        company: {
          __typename: 'Company',
          companyName: 'LORENZ Life Sciences Group',
          entityPageId: generateIncrementalId(),
          entityPage: {
            id: generateIncrementalId(),
            slug: 'lorenz-life-sciences-group',
            coverImage: [
              {
                url: '/test-assets/header-desktop-984x480.png',
              },
            ],
          },
          industry: {
            localizationValue: 'Retail',
            __typename: 'Industry',
          },
          logos: {
            logo256px: '/test-assets/256x256xdddx000.png',
          },
          address: {
            city: 'Gutach im Breisgau',
          },
          kununuData: {
            companyProfileUrl: 'https://www.kununu.com/de/new-work',
            ratingCount: 881,
            ratingAverage: 2.5,
          },
        },
        fallbackCompany: null,
        takenJobLocation: null,
        ...mockSkills(12, 10),
        experienceMatchedScore: 4,
        experienceTotal: 8,
        preferredIndustry: {
          id: '90100.a8b7b7',
          value: 'Software',
          __typename: 'DreamJobIndusty',
        },
        preferredDiscipline: {
          id: null,
          value: 'ITSoftwareDevelopment',
          __typename: 'DreamJobDiscipline',
        },
        ...mockSimilarMembers(1),
        salary: {
          currency: Types.Currency.Eur,
          minimum: 100000,
          median: 140000,
          maximum: 190000,
          __typename: 'SalaryEstimate',
        },
      },
    ],
  });

export const getDreamMachineVisitorsMocks =
  (): GetDreamMachineVisitorsQuery => {
    const images = [
      '/test-assets/profile-image-1.jpeg',
      '/test-assets/profile-image-2.jpeg',
      '/test-assets/profile-image-3.jpeg',
      '/test-assets/profile-image-4.jpeg',
      '/test-assets/profile-image-1.jpeg',
      '/test-assets/profile-image-2.jpeg',

      '/test-assets/profile-image-3.jpeg',
      '/test-assets/profile-image-3.jpeg',
      '/test-assets/profile-image-3.jpeg',
      '/test-assets/profile-image-3.jpeg',
      '/test-assets/profile-image-3.jpeg',
    ];
    return {
      viewer: {
        id: '1',
        visitorsList: {
          total: images.length,
          edges: images.map((url, idx) => ({
            __typename: 'ProfileVisitorsEdge',
            node: {
              __typename: 'ProfileVisit',
              id: `visitor-${idx + 1}`,
              profileVisitor: {
                __typename: 'FencedProfileVisitor',
                profileImage: [
                  {
                    url,
                  },
                ],
              },
            },
          })),
        },
      },
    };
  };
